import {
  ChevronDownIcon,
  CloudUploadIcon,
  FileDiffIcon,
  GitBranchIcon,
  GitCommitIcon,
  GitCompareArrowsIcon,
  MonitorIcon,
} from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "~/lib/utils";
import { Button } from "./ui/button";
import { Popover, PopoverPopup, PopoverTrigger } from "./ui/popover";
import type { EnvironmentPanelAction, EnvironmentPanelModel } from "./EnvironmentPanel.logic";

interface EnvironmentPanelContentProps {
  readonly model: EnvironmentPanelModel;
  readonly onAction: (action: EnvironmentPanelAction) => void;
  readonly onCompare: () => void;
}

interface EnvironmentPanelProps extends EnvironmentPanelContentProps {
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
}

function EnvironmentPanelRow(props: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly value: ReactNode;
  readonly className?: string;
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-[1rem_minmax(0,1fr)_auto] items-center gap-2 px-3 py-2",
        props.className,
      )}
    >
      <span className="text-muted-foreground" aria-hidden>
        {props.icon}
      </span>
      <span className="text-xs text-muted-foreground">{props.label}</span>
      <span className="min-w-0 max-w-48 truncate text-right text-xs font-medium text-foreground">
        {props.value}
      </span>
    </div>
  );
}

function actionIcon(action: EnvironmentPanelAction) {
  return action.kind === "init" ? (
    <GitBranchIcon className="size-3.5" />
  ) : action.kind === "publish" || (action.kind === "git" && action.label === "Push") ? (
    <CloudUploadIcon className="size-3.5" />
  ) : (
    <GitCommitIcon className="size-3.5" />
  );
}

function EnvironmentPanelInteractiveRow(props: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly value: string;
  readonly disabled: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={props.disabled}
      onClick={props.onClick}
      className="grid w-full grid-cols-[1rem_minmax(0,1fr)_auto] items-center gap-2 px-3 py-2 text-left outline-none transition-colors enabled:hover:bg-accent enabled:focus-visible:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
    >
      <span className="text-muted-foreground" aria-hidden>
        {props.icon}
      </span>
      <span className="text-xs text-muted-foreground">{props.label}</span>
      <span className="max-w-48 truncate text-right text-xs font-medium">{props.value}</span>
    </button>
  );
}

export function EnvironmentPanelContent({
  model,
  onAction,
  onCompare,
}: EnvironmentPanelContentProps) {
  return (
    <div
      className="w-80"
      data-environment-panel-content
      data-environment-panel-availability={model.availability}
      aria-busy={model.availability === "loading"}
    >
      <h3 className="px-3 pb-2 pt-3 text-sm font-semibold">Environment</h3>
      <div className="divide-y divide-border/70 border-y border-border/70">
        <EnvironmentPanelRow
          icon={<FileDiffIcon className="size-3.5" />}
          label="Changes"
          value={
            model.changes.insertions === null || model.changes.deletions === null ? (
              model.changes.label
            ) : (
              <span
                aria-label={`${model.changes.insertions} insertions and ${model.changes.deletions} deletions`}
              >
                <span className="text-success">+{model.changes.insertions}</span>{" "}
                <span className="text-destructive">-{model.changes.deletions}</span>
              </span>
            )
          }
        />
        <EnvironmentPanelRow
          icon={<MonitorIcon className="size-3.5" />}
          label="Environment/workspace"
          value={
            <span className="flex min-w-0 flex-col items-end">
              <span className="max-w-48 truncate">{model.environmentLabel}</span>
              <span className="max-w-48 truncate font-normal text-muted-foreground">
                {model.workspaceLabel}
              </span>
            </span>
          }
        />
        <div data-environment-panel-branch="read-only">
          <EnvironmentPanelRow
            icon={<GitBranchIcon className="size-3.5" />}
            label="Active branch"
            value={model.branchLabel}
          />
        </div>
        <EnvironmentPanelInteractiveRow
          icon={actionIcon(model.action)}
          label={model.action.kind === "init" ? "Version control" : "Commit or push"}
          value={model.action.label}
          disabled={model.action.disabled}
          onClick={() => onAction(model.action)}
        />
        <EnvironmentPanelInteractiveRow
          icon={<GitCompareArrowsIcon className="size-3.5" />}
          label="Compare branch"
          value={model.canCompare ? "Open comparison" : "Unavailable"}
          disabled={!model.canCompare}
          onClick={onCompare}
        />
      </div>
      {model.unavailableReason ? (
        <p className="px-3 pt-2 text-xs text-muted-foreground">{model.unavailableReason}</p>
      ) : null}
    </div>
  );
}

export function EnvironmentPanel({
  model,
  onAction,
  onCompare,
  open,
  onOpenChange,
}: EnvironmentPanelProps) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger render={<Button aria-label="Environment" variant="outline" size="xs" />}>
        <MonitorIcon className="size-3.5" aria-hidden />
        <span className="sr-only @3xl/header-actions:not-sr-only">Environment</span>
        {model.changes.insertions !== null && model.changes.deletions !== null ? (
          <span className="hidden gap-1 font-mono text-[10px] @4xl/header-actions:inline-flex">
            <span className="text-success">+{model.changes.insertions}</span>
            <span className="text-destructive">-{model.changes.deletions}</span>
          </span>
        ) : null}
        <ChevronDownIcon className="size-3 opacity-60" aria-hidden />
      </PopoverTrigger>
      <PopoverPopup
        align="end"
        side="bottom"
        className="max-w-[calc(100vw-1rem)] p-0"
        viewportClassName="p-0"
      >
        <EnvironmentPanelContent model={model} onAction={onAction} onCompare={onCompare} />
      </PopoverPopup>
    </Popover>
  );
}
